import { useState, useEffect } from 'react'
import {
  Bot,
  Sparkles,
  Zap,
  Server,
  FileText,
  Settings,
  AlertCircle,
  Terminal,
  Loader2,
  BookOpen,
  Store,
  RefreshCw,
} from 'lucide-react'
import { getClaudeConfig, pushClaudeConfig, type ClaudeConfigOverview } from '../../lib/api'

export default function AdminClaudeCode() {
  const [config, setConfig] = useState<ClaudeConfigOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pushing, setPushing] = useState(false)
  const [pushResult, setPushResult] = useState<string | null>(null)

  useEffect(() => {
    loadConfig()
  }, [])

  async function loadConfig() {
    setLoading(true)
    setError(null)
    try {
      const data = await getClaudeConfig()
      setConfig(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 rounded-xl border border-destructive/50 bg-destructive/10">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-destructive" />
          <h2 className="text-lg font-semibold text-destructive">Failed to Load Configuration</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <button
          onClick={loadConfig}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!config) return null

  const allAgents = [
    ...config.agents.map((a) => ({ ...a, badge: null })),
    ...config.gsd_agents.map((a) => ({ ...a, badge: 'GSD' })),
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Claude Code Configuration</h1>
          <p className="text-muted-foreground">
            Configuration sourced from version-controlled files in{' '}
            <code className="px-1.5 py-0.5 rounded bg-secondary text-xs font-mono">config/claude/</code>
            ,{' '}
            <code className="px-1.5 py-0.5 rounded bg-secondary text-xs font-mono">config/gsd/</code>
            {' '}and{' '}
            <code className="px-1.5 py-0.5 rounded bg-secondary text-xs font-mono">config/marketplace/</code>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {pushResult && (
            <span className="text-sm text-muted-foreground">{pushResult}</span>
          )}
          <button
            onClick={async () => {
              setPushing(true)
              setPushResult(null)
              try {
                await pushClaudeConfig()
                setPushResult('Config pushed to all workspaces')
                setTimeout(() => setPushResult(null), 5000)
              } catch (err) {
                setPushResult(err instanceof Error ? err.message : 'Push failed')
              } finally {
                setPushing(false)
              }
            }}
            disabled={pushing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {pushing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Push to Workspaces
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Agents Section */}
        <Section
          icon={<Bot className="w-5 h-5" />}
          title="Agents"
          count={allAgents.length}
        >
          {allAgents.length === 0 ? (
            <EmptyState label="agents" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {allAgents.map((agent) => (
                <div key={`${agent.badge || 'claude'}-${agent.filename}`} className="p-4 rounded-lg border border-border bg-card">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{agent.name}</span>
                    {agent.badge && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-500/10 text-blue-500 uppercase">
                        {agent.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{agent.description}</p>
                  <span className="text-[11px] font-mono text-muted-foreground/70">{agent.filename}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Skills Section */}
        <Section
          icon={<Sparkles className="w-5 h-5" />}
          title="Skills"
          count={config.skills.length}
        >
          {config.skills.length === 0 ? (
            <EmptyState label="skills" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.skills.map((skill) => (
                <div key={skill.directory} className="p-4 rounded-lg border border-border bg-card">
                  <span className="font-medium text-sm">{skill.name}</span>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{skill.description}</p>
                  <span className="text-[11px] font-mono text-muted-foreground/70">{skill.directory}/</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Marketplace Section */}
        <Section
          icon={<Store className="w-5 h-5" />}
          title={config.marketplace?.name ? `Marketplace: ${config.marketplace.name}` : 'Marketplace'}
          count={config.marketplace?.plugins?.length ?? 0}
        >
          {!config.marketplace?.available ? (
            <EmptyState label="marketplace plugins" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.marketplace.plugins.map((plugin) => (
                <div key={plugin.name} className="p-4 rounded-lg border border-border bg-card">
                  <span className="font-medium text-sm">{plugin.name}</span>
                  {plugin.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{plugin.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Hooks Section */}
        <Section
          icon={<Zap className="w-5 h-5" />}
          title="Hooks"
          count={config.hooks.length}
        >
          {config.hooks.length === 0 ? (
            <EmptyState label="hooks" />
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/50">
                    <th className="text-left px-4 py-2 font-medium">Event</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">Command</th>
                  </tr>
                </thead>
                <tbody>
                  {config.hooks.map((hook, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2 font-mono text-xs">{hook.event}</td>
                      <td className="px-4 py-2 text-muted-foreground">{hook.type}</td>
                      <td className="px-4 py-2 font-mono text-xs truncate max-w-[300px]">{hook.command}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* MCP Servers Section */}
        <Section
          icon={<Server className="w-5 h-5" />}
          title="MCP Servers"
          count={config.mcp_servers.length}
        >
          {config.mcp_servers.length === 0 ? (
            <EmptyState label="MCP servers" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {config.mcp_servers.map((server) => (
                <div key={server.name} className="p-4 rounded-lg border border-border bg-card">
                  <span className="font-medium text-sm">{server.name}</span>
                  <div className="mt-2 space-y-1">
                    <div className="text-xs">
                      <span className="text-muted-foreground">Command: </span>
                      <code className="font-mono">{server.command}</code>
                    </div>
                    {server.args.length > 0 && (
                      <div className="text-xs">
                        <span className="text-muted-foreground">Args: </span>
                        <code className="font-mono">{server.args.join(' ')}</code>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Settings Section */}
        <Section
          icon={<Settings className="w-5 h-5" />}
          title="Settings"
          count={Object.keys(config.settings).length}
        >
          {Object.keys(config.settings).length === 0 ? (
            <EmptyState label="settings" />
          ) : (
            <div className="p-4 rounded-lg border border-border bg-card">
              <div className="space-y-2">
                {Object.entries(config.settings).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center text-sm py-1 border-b border-border last:border-0">
                    <span className="font-mono text-muted-foreground">{key}</span>
                    <span className="font-medium">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Template Section */}
        <Section
          icon={<FileText className="w-5 h-5" />}
          title="Template"
          count={config.template ? 1 : 0}
        >
          {config.template ? (
            <div className="p-4 rounded-lg border border-border bg-card inline-block">
              <span className="font-medium text-sm">{config.template.filename}</span>
              <p className="text-xs text-muted-foreground mt-1">{config.template.lines} lines</p>
            </div>
          ) : (
            <EmptyState label="templates" />
          )}
        </Section>

        {/* Rules Section */}
        <Section
          icon={<BookOpen className="w-5 h-5" />}
          title="Rules"
          count={config.rules?.length ?? 0}
        >
          {!config.rules || config.rules.length === 0 ? (
            <EmptyState label="rules" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.rules.map((rule) => (
                <div key={rule.filename} className="p-4 rounded-lg border border-border bg-card">
                  <span className="font-medium text-sm">{rule.name}</span>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{rule.description}</p>
                  <span className="text-[11px] font-mono text-muted-foreground/70">{rule.filename}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* GSD Commands Section */}
        <Section
          icon={<Terminal className="w-5 h-5" />}
          title="GSD Commands"
          count={config.gsd_commands.length}
        >
          {config.gsd_commands.length === 0 ? (
            <EmptyState label="GSD commands" />
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/50">
                    <th className="text-left px-4 py-2 font-medium">Command</th>
                    <th className="text-left px-4 py-2 font-medium">File</th>
                  </tr>
                </thead>
                <tbody>
                  {config.gsd_commands.map((cmd) => (
                    <tr key={cmd.name} className="border-t border-border">
                      <td className="px-4 py-2 font-medium">{cmd.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{cmd.filename}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  )
}

function Section({
  icon,
  title,
  count,
  children,
}: {
  icon: React.ReactNode
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-muted-foreground">{icon}</span>
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-secondary text-muted-foreground">
          {count}
        </span>
      </div>
      {children}
    </div>
  )
}

function EmptyState({ label }: { label: string }) {
  return (
    <p className="text-sm text-muted-foreground italic">No {label} configured</p>
  )
}
